import { ItemKey } from '@cyberscore/contracts';
import { Factory, FileSignature, GitBranch, HelpCircle, Layers, Plus, Scale, Send, X } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { ApiError } from '../../lib/api';
import { href, navigate } from '../../lib/router';
import { useCreateItem, useItems, usePendingRelease, useStudioAccess } from './api';
import { type ItemSummary } from './journey';

export const SCOPE_LABEL: Record<string, string> = {
  platform: 'Platform',
  country: 'Country',
  industry: 'Industry',
  tenant: 'Your organisation',
  product: 'Product',
  customer: 'Customer',
};

export function when(iso: string) {
  return new Date(iso).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * The studio (tasks 41–44): configuration as drafts, published through maker-checker releases. This page
 * lists the journeys and stages (each journey opens in the journey builder) and leads to the question and rule
 * editors.
 * `embedded`: inside Tenant 360's tab (task 102), under a smaller heading.
 */
export function StudioPage({ embedded = false }: { embedded?: boolean } = {}) {
  const access = useStudioAccess();
  const journeys = useItems('journey');
  const stages = useItems('stage');
  const profiles = useItems('industry_profile');
  const forms = useItems('insurer_form');
  const [creating, setCreating] = useState(false);
  const [creatingForm, setCreatingForm] = useState(false);
  const pending = usePendingRelease().data;

  return (
    <>
      <div className={`page-head row-between${embedded ? ' embedded' : ''}`}>
        <div>
          {embedded ? <h2>Studio</h2> : <h1>Studio</h1>}
          <p>
            Journeys, questions, rules and insurer forms. Changes are drafts until a release is approved by a
            second person.
          </p>
        </div>
        <div className="row gap wrap">
          <a className="btn" href={href.questions()}>
            <HelpCircle size={16} /> Questions
          </a>
          <a className="btn" href={href.rules()}>
            <Scale size={16} /> Rules
          </a>
          <a className="btn" href={href.releases()}>
            <Send size={16} /> Releases
          </a>
          {access.edits && (
            <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
              <Plus size={16} /> New journey
            </button>
          )}
        </div>
      </div>

      {pending && (
        <a className="notice neutral block-link mb" href={href.releases()}>
          A release is waiting for approval: “{pending.note}”
        </a>
      )}
      {creating && <NewJourney stages={stages.data ?? []} onClose={() => setCreating(false)} />}

      <section className="card">
        <div className="card-head">
          <div className="card-title row gap">
            <GitBranch size={16} /> Journeys
          </div>
        </div>
        <ItemTable q={journeys} open={(i) => navigate(href.journey(i.id))} empty="No journeys yet." />
      </section>

      <section className="card mt">
        <div className="card-head">
          <div className="card-title row gap">
            <Layers size={16} /> Stages
          </div>
          <span className="muted small">Edited inside a journey</span>
        </div>
        <ItemTable q={stages} empty="No stages yet." />
      </section>

      <section className="card mt">
        <div className="card-head">
          <div className="card-title row gap">
            <Factory size={16} /> Industry profiles
          </div>
          <span className="muted small">
            Journey, depth, weights, kill switches and minimum band per industry
          </span>
        </div>
        <ItemTable q={profiles} link={href.profile} empty="No industry profiles yet." />
      </section>

      <section className="card mt">
        <div className="card-head">
          <div className="card-title row gap">
            <FileSignature size={16} /> Insurer forms
          </div>
          <div className="row gap">
            <span className="muted small">
              Fields, where their answers come from, eligibility, premiums and the PDF
            </span>
            {access.edits && (
              <button type="button" className="btn btn-sm" onClick={() => setCreatingForm(true)}>
                <Plus size={14} /> New insurer form
              </button>
            )}
          </div>
        </div>
        {creatingForm && <NewInsurerForm onClose={() => setCreatingForm(false)} />}
        <ItemTable q={forms} link={href.insurerForm} empty="No insurer forms yet." />
      </section>
    </>
  );
}

function ItemTable({
  q,
  open,
  link,
  empty,
}: {
  q: ReturnType<typeof useItems>;
  open?: (i: ItemSummary) => void;
  /** Rows open this page; without it, `open` decides and the name links to the journey builder. */
  link?: (id: string) => string;
  empty: string;
}) {
  const go = open ?? (link ? (i: ItemSummary) => navigate(link(i.id)) : undefined);
  if (q.isPending) return <div className="empty compact">Loading…</div>;
  if (q.isError) return <div className="empty compact">{q.error.message}</div>;
  if (!q.data.length) return <div className="empty compact">{empty}</div>;
  return (
    <div className="table-wrap">
      <table className="grid">
        <thead>
          <tr>
            <th>Name</th>
            <th>Key</th>
            <th>Scope</th>
            <th>Revision</th>
            <th>Updated</th>
          </tr>
        </thead>
        <tbody>
          {q.data.map((i) => (
            <tr key={i.id} className={go ? 'clickable' : undefined} onClick={go && (() => go(i))}>
              <td>
                {go ? (
                  <a
                    href={(link ?? href.journey)(i.id)}
                    className="cell-title"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {i.title || i.key}
                  </a>
                ) : (
                  i.title || i.key
                )}
              </td>
              <td className="mono small">{i.key}</td>
              <td>
                <span className="pill neutral">{SCOPE_LABEL[i.scope_level] ?? i.scope_level}</span>
              </td>
              <td className="num">{i.revision}</td>
              <td className="muted">{when(i.updated_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** A journey needs a key, a name and its first stage; the rest is built in the journey builder. */
function NewJourney({ stages, onClose }: { stages: ItemSummary[]; onClose: () => void }) {
  const create = useCreateItem();
  const [form, setForm] = useState({ key: '', name: '', stage: '' });
  const [error, setError] = useState<string | null>(null);
  const keys = [...new Set(stages.map((s) => s.key))];
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const key = ItemKey.safeParse(form.key);
    if (!key.success) return setError(`Key: ${key.error.issues[0]!.message}`);
    if (!form.name.trim() || !form.stage) return setError('Give it a name and a first stage.');
    try {
      const row = await create.mutateAsync({
        kind: 'journey',
        key: form.key,
        body: { name: form.name.trim(), stages: [{ stage: form.stage, after: [] }] },
      });
      navigate(href.journey(row.id));
    } catch (err) {
      setError(err instanceof ApiError ? (err.issues[0]?.message ?? err.message) : 'Could not create it');
    }
  };
  return (
    <form className="card form-card mb" onSubmit={submit} noValidate>
      <div className="card-head">
        <div className="card-title">New journey</div>
        <button type="button" className="btn btn-icon btn-ghost" aria-label="Close" onClick={onClose}>
          <X size={16} />
        </button>
      </div>
      <div className="form-body">
        <div className="two">
          <div className="field">
            <label htmlFor="nj-name">Name</label>
            <input id="nj-name" className="input" autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Retail SME, Malaysia" />
          </div>
          <div className="field">
            <label htmlFor="nj-key">Key</label>
            <input id="nj-key" className="input mono" value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} placeholder="JRN-SME-RETAIL-MY" />
            <span className="hint">Letters, digits, _ . - ; can’t be changed later.</span>
          </div>
        </div>
        <div className="field">
          <label htmlFor="nj-stage">First stage</label>
          <select id="nj-stage" className="input" value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })}>
            <option value="">Choose a stage…</option>
            {keys.map((k) => (
              <option key={k} value={k}>
                {k} · {stages.find((s) => s.key === k)?.title}
              </option>
            ))}
          </select>
        </div>
        {error && (
          <div className="error" role="alert">
            {error}
          </div>
        )}
      </div>
      <div className="form-foot">
        <button type="button" className="btn" onClick={onClose}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={create.isPending}>
          Create journey
        </button>
      </div>
    </form>
  ); // prettier-ignore
}

/**
 * A new insurer form (task 96) needs a key and who it is from; its sections and fields are built in the
 * form editor. It starts with the proposer's name, filled from the company's details.
 */
function NewInsurerForm({ onClose }: { onClose: () => void }) {
  const create = useCreateItem();
  const [form, setForm] = useState({ key: '', insurer: '', product: '', code: '' });
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    // The forms question offers each form by its key, and option values allow only lowercase and _.
    if (!/^[a-z0-9][a-z0-9_]{0,63}$/.test(form.key)) return setError('Key: lowercase letters, digits and _');
    if (!form.insurer.trim() || !form.product.trim() || !form.code.trim())
      return setError('Give the insurer, the product and the form’s number.');
    try {
      const row = await create.mutateAsync({
        kind: 'insurer_form',
        key: form.key,
        body: {
          insurer: form.insurer.trim(),
          product: form.product.trim(),
          form_code: form.code.trim(),
          sections: [{ key: 'proposer', title: 'Proposer', fields: [{ key: 'proposer_name', ref: '1', label: 'Name of proposer', type: 'text', source: { from: 'context', key: 'company_name' } }] }], // prettier-ignore
        },
      });
      navigate(href.insurerForm(row.id));
    } catch (err) {
      setError(err instanceof ApiError ? (err.issues[0]?.message ?? err.message) : 'Could not create it');
    }
  };
  return (
    <form className="form-body form-inline-card" onSubmit={submit} noValidate aria-label="New insurer form">
      <div className="two">
        <div className="field">
          <label htmlFor="nf-insurer">Insurer</label>
          <input id="nf-insurer" className="input" autoFocus value={form.insurer} onChange={(e) => setForm({ ...form, insurer: e.target.value })} placeholder="e.g. Allianz General Insurance Malaysia" />
        </div>
        <div className="field">
          <label htmlFor="nf-product">Product</label>
          <input id="nf-product" className="input" value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} placeholder="e.g. Cyber Protect" />
        </div>
      </div>
      <div className="two">
        <div className="field">
          <label htmlFor="nf-code">Form number and edition</label>
          <input id="nf-code" className="input mono" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="e.g. CP-PF-0125" />
        </div>
        <div className="field">
          <label htmlFor="nf-key">Key</label>
          <input id="nf-key" className="input mono" value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} placeholder="allianz_cyber_protect" />
          <span className="hint">Lowercase letters, digits and _; can’t be changed later.</span>
        </div>
      </div>
      {error && (
        <div className="error" role="alert">
          {error}
        </div>
      )}
      <div className="row gap">
        <button type="button" className="btn" onClick={onClose}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={create.isPending}>
          Create the form
        </button>
      </div>
    </form>
  ); // prettier-ignore
}
